import { RadioButton } from "@codat/orchard-ui";
import classNames from "classnames";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import Button from "components/Button";
import TextInput from "components/TextInput";

import { AuthHeader, AuthHeaderScheme } from "../AuthHeaderType";
import styles from "./Form.module.css";

export const Form: React.FC<{
  defaultValues?: AuthHeader;
  isLoading: boolean;
  isUpdating: boolean;
  onSubmit: (values: AuthHeader) => void;
}> = ({ defaultValues, isLoading, isUpdating, onSubmit }) => {
  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
    resetField,
  } = useForm<AuthHeader>({
    mode: "onSubmit",
    shouldFocusError: false,
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const validateAuthInputValue = (
    currentScheme: AuthHeaderScheme,
    applicableScheme: AuthHeaderScheme,
    value?: string
  ) => {
    if (currentScheme !== applicableScheme) {
      return;
    }
    if (!value?.trim()) {
      return "Required";
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="scheme"
        render={({ field }) => (
          <>
            <div className={styles.radioButtonsContainer}>
              <RadioButton
                isChecked={field.value === "None"}
                isSkeleton={isLoading}
                id="auth-header-radio-none"
                data-testid="auth-header-radio-none"
                disabled={isUpdating}
                value="None"
                label="No authorization"
                {...register("scheme")}
              />
            </div>
            <div className={styles.radioButtonsContainer}>
              <RadioButton
                isChecked={field.value === "Basic"}
                isSkeleton={isLoading}
                id="auth-header-radio-basic"
                data-testid="auth-header-radio-basic"
                disabled={isUpdating}
                value="Basic"
                label="Basic"
                {...register("scheme")}
              />
            </div>
            <div
              className={classNames(styles.textInputContainer, {
                [styles.hidden]: field.value !== "Basic",
              })}
            >
              <TextInput
                inputName="Basic Auth Username"
                isSkeleton={isLoading}
                id="auth-header-basic-username"
                data-testid="auth-header-basic-username"
                disabled={isUpdating}
                label="Username"
                error={!!errors.basic?.username}
                errorMessage={errors.basic?.username?.message}
                {...register("basic.username", {
                  validate: (val) =>
                    validateAuthInputValue(field.value, "Basic", val),
                })}
              />
              <TextInput
                inputName="Basic Auth Password"
                isSkeleton={isLoading}
                id="auth-header-basic-password"
                data-testid="auth-header-basic-password"
                disabled={isUpdating}
                label="Password"
                error={!!errors.basic?.password}
                errorMessage={errors.basic?.password?.message}
                onFocus={() => {
                  resetField("basic.password", { defaultValue: "" });
                }}
                {...register("basic.password", {
                  validate: (val) =>
                    validateAuthInputValue(field.value, "Basic", val),
                })}
              />
            </div>
            <div className={styles.radioButtonsContainer}>
              <RadioButton
                isChecked={field.value === "Bearer"}
                isSkeleton={isLoading}
                id="auth-header-radio-bearer"
                data-testid="auth-header-radio-bearer"
                disabled={isUpdating}
                value="Bearer"
                label="Bearer"
                {...register("scheme")}
              />
            </div>
            <div
              className={classNames(styles.textInputContainer, {
                [styles.hidden]: field.value !== "Bearer",
              })}
            >
              <TextInput
                inputName="Bearer Auth Token"
                isSkeleton={isLoading}
                id="auth-header-bearer-token"
                data-testid="auth-header-bearer-token"
                disabled={isUpdating}
                label="Token"
                error={!!errors.bearer}
                errorMessage={errors.bearer?.message}
                {...register("bearer", {
                  validate: (val) =>
                    validateAuthInputValue(field.value, "Bearer", val),
                })}
              />
            </div>
            <div
              className={classNames(styles.radioButtonsContainer, {
                [styles.hidden]: defaultValues?.scheme !== "Custom",
              })}
            >
              <RadioButton
                isSkeleton={isLoading}
                id="auth-header-radio-custom"
                data-testid="auth-header-radio-custom"
                disabled={isUpdating}
                value="Custom"
                label="Custom"
                {...register("scheme")}
              />
            </div>
            <div
              className={classNames(styles.textInputContainer, {
                [styles.hidden]: field.value !== "Custom",
              })}
            >
              <TextInput
                inputName="Custom Auth Value"
                isSkeleton={isLoading}
                id="auth-header-custom"
                data-testid="auth-header-custom"
                disabled={isUpdating}
                label="Value"
                error={!!errors.custom}
                errorMessage={errors.custom?.message}
                {...register("custom", {
                  validate: (val) =>
                    validateAuthInputValue(field.value, "Custom", val),
                })}
              />
            </div>
          </>
        )}
      />
      <Button
        type="submit"
        className={styles.button}
        buttonName="Save"
        label="Save"
        data-testid="save-button"
        isSkeleton={isLoading}
        isLoading={isUpdating}
        spinnerAlign="left"
      />
    </form>
  );
};
